import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import {
  Boxes,
  ChevronRight,
  Copy,
  Image as ImageIcon,
  MoreHorizontal,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  useDeleteItems,
  useInventoryLevels,
  useItemImageSignedUrl,
  useItemKinds,
  useItems,
  useOnOrderByItem,
  usePrimarySupplierByItem,
  useUpdateItem,
  type Item,
} from '@/features/inventory/hooks';
import { ITEM_STATUS_OPTIONS, getItemStatusMeta } from '@/features/inventory/itemStatus';
import type { ItemPrimarySupplier } from '@/features/inventory/primarySupplier';
import { NewItemDialog } from '@/components/inventory/NewItemDialog';
import { ImportItemsDialog } from '@/components/inventory/ImportItemsDialog';
import { DuplicateItemDialog } from '@/components/inventory/DuplicateItemDialog';
import {
  EditableCurrency,
  EditableNumber,
  EditableText,
} from '@/components/inventory/EditableItemField';
import { ItemStatusControl } from '@/components/inventory/ItemStatusControl';
import { KindBadge, KindPicker } from '@/components/inventory/KindPicker';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatCurrency, formatDateOnly, formatUnitCurrency } from '@/lib/formatters';

// Local helper — `mobileCard.amount.value` displays the retail
// price as a compact tabular-nums number. We reuse the shared
// `formatCurrency` for consistency with the desktop column.
const formatCurrencyForMobile = formatCurrency;

const SHOW_ARCHIVED_KEY = 'catalog-items-show-archived';

/**
 * One grid row: the item plus the supplier the grid shows for it. The
 * rollup is merged into the row (rather than read from a map inside the
 * column accessors) because TanStack caches accessor values per row
 * until `data` changes — a map that resolves after the items would
 * leave the Supplier facet, sort and search stuck on empty values.
 */
type CatalogRow = Item & { primarySupplier: ItemPrimarySupplier | null };

/**
 * 32px square thumbnail for the row's primary product photo. Pulls a
 * short-lived signed URL via the same hook the detail page uses; the
 * `item-images` bucket is private so we can't just point an <img> at
 * a public URL. Falls back to a neutral placeholder when an item has
 * no photo uploaded yet so every row stays visually aligned.
 */
function ItemThumbnail({
  itemId,
  imagePath,
}: {
  itemId: string;
  imagePath: string | null;
}) {
  const { data: imageUrl, isLoading } = useItemImageSignedUrl(itemId, imagePath);
  if (!imagePath) {
    return (
      <div
        className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-secondary/30 text-muted-foreground"
        aria-hidden
      >
        <ImageIcon className="h-4 w-4" />
      </div>
    );
  }
  if (isLoading || !imageUrl) {
    return <Skeleton className="h-8 w-8 rounded-md" />;
  }
  return (
    <img
      src={imageUrl}
      alt=""
      loading="lazy"
      className="h-8 w-8 rounded-md border border-border bg-secondary/30 object-cover"
    />
  );
}

/**
 * Props for the items grid surface.
 *
 * The page is rendered two ways:
 *   * `/catalog/items` — no `kindSlug`, shows every SKU regardless of
 *     kind. The "Kind" column + faceted filter are visible so the
 *     operator can scope by kind from inside the grid.
 *   * `/catalog/items/kind/:kindSlug` — the per-kind variant rendered
 *     by `KindItemsPage`. The list is server-filtered to that kind,
 *     the redundant Kind column / filter are hidden, the page header
 *     swaps in the kind's label + coloured badge, and the
 *     "Add item" dialog pre-selects this kind so a new SKU lands in
 *     the right bucket without an extra click.
 *
 * Column preferences (order, width, visibility) are persisted per
 * `tableId`, so the kind-specific variants get their own bucket —
 * an operator who hides Cost on the "Raw materials" view won't lose
 * it on the all-items grid.
 */
export interface ItemsPageProps {
  /** When set, filter the grid to a single kind slug. */
  kindSlug?: string;
}

export function ItemsPage({ kindSlug }: ItemsPageProps = {}) {
  const navigate = useNavigate();
  const { role } = useAuth();
  const { data, isLoading } = useItems(kindSlug ? { kind: kindSlug } : undefined);
  const { data: kindCatalog } = useItemKinds();
  // Live rollup of quantity per item across every non-transit location.
  // Matches the "On-hand" summary on the item detail Inventory tab so
  // the catalog grid and the 360 view never disagree.
  const { data: inventoryLevels } = useInventoryLevels();
  const { data: onOrderByItemId } = useOnOrderByItem();
  // Who we buy each item from, merged into each row below for the
  // Primary supplier / Supplier part # columns. Same rows the detail
  // page's Suppliers tab shows.
  const { data: primarySupplierByItemId } = usePrimarySupplierByItem();
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState<Item | null>(null);
  const updateItem = useUpdateItem();
  const deleteItems = useDeleteItems();

  // Bulk-delete confirmation. We capture the selected rows + a reset
  // callback when the operator clicks "Delete" in the bulk-action bar
  // so the dialog can show what's about to be removed and clear the
  // selection once the delete lands.
  const [pendingDelete, setPendingDelete] = useState<{
    items: Item[];
    clearSelection: () => void;
  } | null>(null);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { items, clearSelection } = pendingDelete;
    try {
      await deleteItems.mutateAsync({ ids: items.map((i) => i.id) });
      toast.success(
        items.length === 1
          ? `Deleted “${items[0].name}”`
          : `Deleted ${items.length} items`,
      );
      clearSelection();
      setPendingDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete items.');
    }
  };

  // The catalog row for the active kind drives the page header
  // (label, description, coloured badge) when we're scoped to a
  // single kind. Falls back to `null` while the catalog is still
  // hydrating so the header doesn't flash a raw slug.
  const activeKindRow = useMemo(
    () => (kindSlug ? (kindCatalog ?? []).find((k) => k.slug === kindSlug) ?? null : null),
    [kindCatalog, kindSlug],
  );

  // The DB's `items_write_admin` policy only lets admin / super_admin
  // create rows, so we hide the entry points for everyone else
  // instead of letting them open the dialog and bounce off RLS. The
  // same role gate controls inline editing on every cell below.
  const canCreate = canWrite(role, 'inventory');
  const canEdit = canCreate;

  // Archived items stay out of the default catalog. The toolbar checkbox
  // is the only way to bring them back into this grid. Preference is
  // remembered, and the first visit hides them.
  const [showArchived, setShowArchived] = useState(() => {
    try {
      return window.localStorage.getItem(SHOW_ARCHIVED_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(SHOW_ARCHIVED_KEY, showArchived ? '1' : '0');
    } catch {
      // Private mode can reject storage; the in-memory choice still applies.
    }
  }, [showArchived]);

  const allItems = useMemo<Item[]>(() => data ?? [], [data]);
  const rows = useMemo<CatalogRow[]>(
    () =>
      (showArchived ? allItems : allItems.filter((item) => item.status !== 'archived')).map(
        (item) => ({
          ...item,
          primarySupplier: primarySupplierByItemId?.get(item.id) ?? null,
        }),
      ),
    [allItems, showArchived, primarySupplierByItemId],
  );
  const archivedCount = useMemo(
    () => allItems.filter((item) => item.status === 'archived').length,
    [allItems],
  );

  // "Components" (slug `raw_material`) are build/buy parts, not sold at
  // retail — so the Retail price column is dropped on that per-kind view.
  const isComponentView = kindSlug === 'raw_material';

  // Total sellable units per item. Transit locations are excluded so
  // stock still on the truck doesn't inflate the catalog figure.
  const onHandByItemId = useMemo(() => {
    const map = new Map<string, number>();
    for (const lvl of inventoryLevels ?? []) {
      if (lvl.locations?.kind === 'transit') continue;
      map.set(lvl.item_id, (map.get(lvl.item_id) ?? 0) + (lvl.quantity ?? 0));
    }
    return map;
  }, [inventoryLevels]);

  const columns = useMemo<ColumnDef<CatalogRow>[]>(() => {
    const cols: ColumnDef<CatalogRow>[] = [
      {
        // `__`-prefixed id marks this as a system column in DataTable —
        // it can't be reordered or resized, so it stays pinned to the
        // very first slot. The number reflects the row's position in
        // the current sorted/filtered/paginated view (so page 2 with
        // 25/page starts at 26), which matches what an operator sees
        // and lets them reference rows by index in conversation.
        id: '__row_number',
        enableSorting: false,
        enableHiding: false,
        header: () => <span className="block text-right">#</span>,
        size: 48,
        minSize: 40,
        cell: ({ row, table }) => {
          const { pageIndex, pageSize } = table.getState().pagination;
          const idxInPage = table
            .getRowModel()
            .rows.findIndex((r) => r.id === row.id);
          if (idxInPage === -1) return null;
          return (
            <span className="block text-right tabular-nums text-xs text-muted-foreground">
              {pageIndex * pageSize + idxInPage + 1}
            </span>
          );
        },
      },
      {
        // System column (see `__row_number` above) — a tiny product
        // photo so operators can scan the grid visually. Pinned to
        // this slot for everyone; not reorderable, hideable, or
        // sortable, same shape as `__open`.
        id: '__thumbnail',
        enableSorting: false,
        enableHiding: false,
        header: () => null,
        size: 56,
        minSize: 56,
        cell: ({ row }) => (
          <ItemThumbnail
            itemId={row.original.id}
            imagePath={row.original.image_path}
          />
        ),
      },
      {
        id: 'sku',
        accessorKey: 'sku',
        header: 'SKU',
        size: 140,
        minSize: 80,
        // Two cell variants by role:
        //   - Read-only role: keep the navigation anchor so right-click
        //     → "open in new tab" still works (this column is the
        //     primary affordance for jumping to the detail page).
        //   - Admin role: render an inline editor — clicking the SKU
        //     opens a text input. Navigation is still one click away
        //     via the chevron column or any non-editable region of the
        //     row, which the row's `onRowClick` picks up.
        // The DB does the heavy lifting on rename: every downstream
        // table (BOMs, POs, SOs, work orders, inventory levels, stock
        // movements, suppliers) FKs to `items.id`, so the new SKU
        // shows up in every joined view automatically.
        cell: ({ row }) => {
          const item = row.original;
          if (!canEdit) {
            return (
              <Link
                to={`/catalog/items/${item.id}`}
                onClick={(e) => e.stopPropagation()}
                className="whitespace-nowrap font-mono text-xs text-foreground hover:underline"
              >
                {item.sku}
              </Link>
            );
          }
          return (
            <EditableText
              value={item.sku}
              variant="mono"
              required
              maxLength={64}
              ariaLabel="SKU"
              canEdit
              density="cell"
              onSave={async (next) => {
                await updateItem.mutateAsync({ id: item.id, patch: { sku: next } });
              }}
            />
          );
        },
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        size: 260,
        minSize: 120,
        cell: ({ row }) => {
          const item = row.original;
          if (!canEdit) {
            return (
              <Link
                to={`/catalog/items/${item.id}`}
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-foreground hover:underline"
              >
                {item.name}
              </Link>
            );
          }
          return (
            <EditableText
              value={item.name}
              required
              maxLength={200}
              ariaLabel="Name"
              canEdit
              density="cell"
              triggerClassName="font-medium"
              onSave={async (next) => {
                await updateItem.mutateAsync({ id: item.id, patch: { name: next } });
              }}
            />
          );
        },
      },
      {
        id: 'parent_sku',
        accessorFn: (r) => r.parent_sku ?? '',
        header: 'Parent SKU',
        size: 140,
        minSize: 80,
        cell: ({ row }) => {
          const item = row.original;
          return (
            <EditableText
              value={item.parent_sku ?? ''}
              variant="mono"
              maxLength={64}
              ariaLabel="Parent SKU"
              canEdit={canEdit}
              density="cell"
              placeholder="—"
              onSave={async (next) => {
                await updateItem.mutateAsync({ id: item.id, patch: { parent_sku: next } });
              }}
            />
          );
        },
      },
      {
        id: 'kind',
        // Every assigned type, so the Type facet matches an item filed
        // under any of them.
        accessorFn: (r) => [r.kind, ...(r.additional_kinds ?? [])],
        sortingFn: (a, b) => a.original.kind.localeCompare(b.original.kind),
        header: 'Type',
        size: 220,
        minSize: 140,
        filterFn: arrIncludesSome,
        // The Type cell is a multi-select of every catalog type. Super
        // admins can mint a new type inline — same component as the
        // detail page, so the checkbox UX stays identical.
        cell: ({ row }) => {
          const item = row.original;
          return (
            <KindPicker
              value={[item.kind, ...(item.additional_kinds ?? [])]}
              preferredPrimary={item.kind}
              canEdit={canEdit}
              density="cell"
              onSave={async (next) => {
                await updateItem.mutateAsync({
                  id: item.id,
                  patch: { kind: next.kind, additional_kinds: next.additional_kinds },
                });
              }}
            />
          );
        },
      },
      {
        id: 'region',
        accessorFn: (r) => r.region ?? '',
        header: 'Region',
        size: 100,
        minSize: 70,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const item = row.original;
          return (
            <EditableText
              value={item.region ?? ''}
              maxLength={32}
              ariaLabel="Region"
              canEdit={canEdit}
              density="cell"
              placeholder="—"
              onSave={async (next) => {
                await updateItem.mutateAsync({ id: item.id, patch: { region: next } });
              }}
            />
          );
        },
      },
      {
        id: 'std_cost_cents',
        accessorKey: 'std_cost_cents',
        header: () => <span className="block text-right">Cost</span>,
        size: 110,
        minSize: 80,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const item = row.original;
          return (
            <EditableCurrency
              value={item.std_cost_cents}
              ariaLabel="Standard cost"
              precise
              canEdit={canEdit}
              density="cell"
              align="right"
              triggerClassName="text-muted-foreground justify-end"
              onSave={async (next) => {
                await updateItem.mutateAsync({
                  id: item.id,
                  patch: { std_cost_cents: next },
                });
              }}
            />
          );
        },
      },
      {
        id: 'list_price_cents',
        accessorKey: 'list_price_cents',
        header: () => <span className="block text-right">Retail price</span>,
        size: 130,
        minSize: 80,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const item = row.original;
          return (
            <EditableCurrency
              value={item.list_price_cents}
              ariaLabel="Retail price"
              canEdit={canEdit}
              density="cell"
              align="right"
              triggerClassName="justify-end"
              onSave={async (next) => {
                await updateItem.mutateAsync({
                  id: item.id,
                  patch: { list_price_cents: next },
                });
              }}
            />
          );
        },
      },
      {
        id: 'workstations',
        // Return the attached station names as an array so the faceted
        // filter (arrIncludesSome) can match on any one of them and the
        // facet options list every distinct station. Curated vocabulary
        // means "Base" is always spelled one way — no "Base"/"Bases" fork.
        accessorFn: (r) => (r.workstations ?? []).map((w) => w.name),
        header: 'Workstations',
        size: 200,
        minSize: 120,
        enableSorting: false,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const stations = row.original.workstations ?? [];
          if (stations.length === 0) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {stations.map((w) => (
                <Badge key={w.id} variant="outline">
                  {w.name}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        id: 'primary_supplier',
        // The vendor name as a plain string so the Supplier facet, the
        // sort and the search box all see the same value.
        accessorFn: (r) => r.primarySupplier?.vendorName ?? '',
        header: 'Primary supplier',
        size: 180,
        minSize: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const supplier = row.original.primarySupplier;
          if (!supplier) return <span className="text-muted-foreground">—</span>;
          if (!supplier.vendorId) {
            return (
              <span
                className="text-xs text-muted-foreground"
                title="Mark one of them primary on the item's Suppliers tab"
              >
                No primary · {supplier.supplierCount} suppliers
              </span>
            );
          }
          return <span>{supplier.vendorName ?? '—'}</span>;
        },
      },
      {
        id: 'supplier_part_number',
        accessorFn: (r) => r.primarySupplier?.supplierPartNumber ?? '',
        header: 'Supplier part #',
        size: 150,
        minSize: 90,
        cell: ({ row }) => {
          const partNumber = row.original.primarySupplier?.supplierPartNumber;
          if (!partNumber) return <span className="text-muted-foreground">—</span>;
          return <span className="whitespace-nowrap font-mono text-xs">{partNumber}</span>;
        },
      },
      {
        id: 'lead_time_worst_case_days',
        accessorKey: 'lead_time_worst_case_days',
        header: () => <span className="block text-right">Worst-case lead</span>,
        size: 130,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const item = row.original;
          return (
            <EditableNumber
              value={item.lead_time_worst_case_days}
              ariaLabel="Worst-case lead time in days"
              canEdit={canEdit}
              density="cell"
              align="right"
              placeholder="—"
              display={
                item.lead_time_worst_case_days == null ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <span className="tabular-nums" title={item.lead_time_text ?? undefined}>
                    {item.lead_time_worst_case_days.toLocaleString()}d
                  </span>
                )
              }
              onSave={async (next) => {
                await updateItem.mutateAsync({
                  id: item.id,
                  patch: { lead_time_worst_case_days: next },
                });
              }}
            />
          );
        },
      },
      {
        id: 'on_hand',
        accessorFn: (r) => onHandByItemId.get(r.id) ?? 0,
        header: () => <span className="block text-right">On-hand</span>,
        size: 120,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const qty = onHandByItemId.get(row.original.id) ?? 0;
          return (
            <span className="block text-right tabular-nums">
              {qty.toLocaleString()}
              <span className="ml-1 text-xs text-muted-foreground">
                {row.original.base_uom}
              </span>
            </span>
          );
        },
      },
      {
        id: 'on_order',
        accessorFn: (r) => onOrderByItemId?.get(r.id)?.quantity ?? 0,
        header: () => <span className="block text-right">On order</span>,
        size: 120,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const qty = onOrderByItemId?.get(row.original.id)?.quantity ?? 0;
          if (!qty) return <span className="block text-right text-muted-foreground">—</span>;
          return (
            <span className="block text-right tabular-nums">
              {qty.toLocaleString()}
              <span className="ml-1 text-xs text-muted-foreground">
                {row.original.base_uom}
              </span>
            </span>
          );
        },
      },
      {
        id: 'next_arrival',
        accessorFn: (r) => onOrderByItemId?.get(r.id)?.nextArrival ?? undefined,
        header: 'Arrival date',
        size: 130,
        minSize: 90,
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        cell: ({ row }) => {
          const onOrder = onOrderByItemId?.get(row.original.id);
          if (!onOrder) return <span className="text-muted-foreground">—</span>;
          if (!onOrder.nextArrival) {
            return <span className="text-xs text-muted-foreground">Not set</span>;
          }
          return <span className="tabular-nums">{formatDateOnly(onOrder.nextArrival)}</span>;
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        minSize: 100,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <ItemStatusControl item={row.original} canEdit={canEdit} />,
      },
      {
        id: '__open',
        enableSorting: false,
        enableHiding: false,
        header: () => null,
        cell: ({ row }) => (
          <Link
            to={`/catalog/items/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            aria-label="Open item"
            className="text-muted-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </Link>
        ),
        size: 32,
      },
    ];
    // Per-row action menu (Duplicate / Delete), admins only. System
    // column like `__open` — pinned, not reorderable / hideable /
    // sortable. We stop propagation on the trigger so opening the menu
    // doesn't also navigate into the row.
    if (canEdit) {
      cols.push({
        id: '__actions',
        enableSorting: false,
        enableHiding: false,
        header: () => null,
        size: 44,
        minSize: 44,
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    aria-label={`Actions for ${item.name}`}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setDuplicateSource(item)}>
                    <Copy className="mr-2 h-4 w-4" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                    onSelect={() =>
                      setPendingDelete({
                        items: [item],
                        clearSelection: () => {},
                      })
                    }
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      });
    }
    // Type stays visible on a per-kind page because an item can belong
    // to several types. On the Components view we drop Retail price,
    // which doesn't apply to a build/buy part.
    return cols.filter((c) => {
      if (isComponentView && c.id === 'list_price_cents') return false;
      return true;
    });
  }, [canEdit, isComponentView, updateItem, onHandByItemId, onOrderByItemId]);
  // `setDuplicateSource` / `setPendingDelete` are stable state setters,
  // so they're intentionally omitted from the dependency list above.

  // Faceted filters at the top of the grid. "Region" auto-derives its
  // option list from the column's unique values; "Status" gets an
  // explicit ordered list so the labels are stable even when only a
  // subset of statuses are present in the visible data. "Kind" is
  // sourced from the live kinds catalog so any kind a super admin has
  // just minted shows up here without a code change. Color is
  // intentionally omitted — product colourway isn't a useful facet for
  // this catalog; operators use On-hand instead.
  const kindOptions = useMemo(
    () =>
      (kindCatalog ?? []).map((k) => ({
        value: k.slug,
        label: k.label,
      })),
    [kindCatalog],
  );

  const facetedFilters: FacetedFilter[] = useMemo(() => {
    const all: FacetedFilter[] = [
      { columnId: 'kind', title: 'Type', options: kindOptions },
      { columnId: 'workstations', title: 'Workstation' },
      { columnId: 'primary_supplier', title: 'Supplier' },
      { columnId: 'region', title: 'Region' },
      {
        columnId: 'status',
        title: 'Status',
        options: showArchived
          ? ITEM_STATUS_OPTIONS
          : ITEM_STATUS_OPTIONS.filter((option) => option.value !== 'archived'),
      },
    ];
    return all;
  }, [kindOptions, showArchived]);

  // Per-kind grids get their own table preference bucket so an
  // operator's column tweaks on (e.g.) "Raw materials" don't bleed
  // back into the all-items view, and vice-versa.
  const tableId = kindSlug ? `inventory-items-kind-${kindSlug}` : 'inventory-items';

  // Header copy + the dialog default kind switch on whether we're in
  // the per-kind variant. `activeKindRow` may briefly be null while
  // the kinds catalog hydrates — fall back to a humanised slug so
  // the title doesn't render the raw `raw_material` token.
  const headerTitle = kindSlug ? (activeKindRow?.label ?? humanizeKindSlug(kindSlug)) : 'Items';

  return (
    <div className="space-y-6">
      <PageHeader
        title={headerTitle}
        actions={
          canCreate ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="h-4 w-4" />
                Import
              </Button>
              <Button onClick={() => setNewOpen(true)}>
                <Plus className="h-4 w-4" />
                Add item
              </Button>
            </div>
          ) : undefined
        }
      />

      <Card>
        <CardContent className="pt-6">
          <DataTable<CatalogRow, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            searchPlaceholder="Search by SKU, name or supplier part #…"
            facetedFilters={facetedFilters}
            initialPageSize={25}
            pageSizes={[10, 25, 50, 100, 200]}
            onRowClick={(r) => navigate(`/catalog/items/${r.id}`)}
            rowClassName={(row) => (row.status === 'archived' ? 'opacity-60' : undefined)}
            toolbarSlot={
              <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-border px-3 text-sm">
                <Checkbox
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                  ariaLabel="Show archived items"
                />
                Show archived
                {!showArchived && archivedCount > 0 && (
                  <span className="tabular-nums text-muted-foreground">({archivedCount})</span>
                )}
              </label>
            }
            // Admins can tick individual rows (or the header checkbox to
            // grab the whole page) and bulk-delete them. The checkbox
            // column + selection toolbar are owned by DataTable; we just
            // opt in and supply the destructive action. Read-only roles
            // never see the affordance — RLS would reject the delete
            // anyway.
            enableRowSelection={canEdit}
            bulkActions={
              canEdit
                ? (table) => (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() =>
                        setPendingDelete({
                          items: table
                            .getSelectedRowModel()
                            .rows.map((r) => r.original),
                          clearSelection: () => table.resetRowSelection(),
                        })
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  )
                : undefined
            }
            // Operators have different mental models for how this grid should
            // be laid out (some want Cost first, some want Retail price first,
            // etc.). Drag a header to reorder; the order persists across
            // reloads via localStorage scoped by `tableId`. Drag the right
            // edge of a header to resize a column — same persistence story.
            enableColumnReordering
            enableColumnResizing
            tableId={tableId}
            // Mobile card layout — title shows the item name (the
            // primary identifier the operator scans for), subtitle
            // surfaces the SKU + (when not kind-scoped) the kind
            // badge, an amount column on the right shows retail
            // price for at-a-glance pricing, and a quiet meta
            // strip beneath fills in Cost / Status. Tapping a card
            // navigates to the detail page; long-press isn't wired
            // because the list isn't bulk-edit friendly today.
            mobileCard={{
              leading: (row) => (
                <ItemThumbnail
                  itemId={row.id}
                  imagePath={row.image_path}
                />
              ),
              title: (row) => row.name,
              subtitle: (row) => (
                <span className="font-mono text-[11px]">{row.sku}</span>
              ),
              dimmed: (row) => row.status === 'archived',
              badges: (row) => {
                const status = getItemStatusMeta(row.status);
                const kindRow = (kindCatalog ?? []).find(
                  (k) => k.slug === row.kind,
                );
                const out: React.ReactNode[] = [
                  <Badge key="status" variant={status.variant}>
                    {status.label}
                  </Badge>,
                ];
                if (!kindSlug && kindRow) {
                  out.push(
                    <KindBadge
                      key="kind"
                      row={kindRow}
                      fallbackSlug={row.kind}
                    />,
                  );
                }
                for (const slug of row.additional_kinds ?? []) {
                  if (slug === kindSlug) continue;
                  out.push(
                    <KindBadge
                      key={`kind-${slug}`}
                      row={(kindCatalog ?? []).find((k) => k.slug === slug) ?? null}
                      fallbackSlug={slug}
                    />,
                  );
                }
                for (const w of row.workstations ?? []) {
                  out.push(
                    <Badge key={`ws-${w.id}`} variant="outline">
                      {w.name}
                    </Badge>,
                  );
                }
                return out;
              },
              amount: (row) =>
                row.list_price_cents
                  ? {
                      value: formatCurrencyForMobile(row.list_price_cents),
                      caption: 'Retail',
                      tone: 'neutral',
                    }
                  : null,
              meta: (row) => {
                const m: { label: string; value: React.ReactNode }[] = [];
                if (row.std_cost_cents) {
                  m.push({
                    label: 'Cost',
                    value: (
                      <span className="tabular-nums">
                        {formatUnitCurrency(row.std_cost_cents)}
                      </span>
                    ),
                  });
                }
                const onHand = onHandByItemId.get(row.id) ?? 0;
                m.push({
                  label: 'On-hand',
                  value: (
                    <span className="tabular-nums">
                      {onHand.toLocaleString()} {row.base_uom}
                    </span>
                  ),
                });
                const onOrder = onOrderByItemId?.get(row.id);
                if (onOrder) {
                  m.push({
                    label: 'On order',
                    value: (
                      <span className="tabular-nums">
                        {onOrder.quantity.toLocaleString()} {row.base_uom}
                        {onOrder.nextArrival && ` · ${formatDateOnly(onOrder.nextArrival)}`}
                      </span>
                    ),
                  });
                }
                // Two cells rather than one combined value: the meta
                // strip is two columns wide and truncates, so a vendor
                // name followed by a part number loses the part number.
                const supplier = row.primarySupplier;
                if (supplier?.vendorId) {
                  m.push({ label: 'Supplier', value: supplier.vendorName ?? '—' });
                  if (supplier.supplierPartNumber) {
                    m.push({
                      label: 'Supplier part #',
                      value: (
                        <span className="font-mono text-[11px]">{supplier.supplierPartNumber}</span>
                      ),
                    });
                  }
                }
                return m;
              },
            }}
            emptyState={
              !showArchived && rows.length === 0 && archivedCount > 0 ? (
                <EmptyState
                  icon={Boxes}
                  title="Archived items are hidden"
                  action={
                    <Button variant="outline" onClick={() => setShowArchived(true)}>
                      Show archived
                    </Button>
                  }
                />
              ) : (
              <EmptyState
                icon={Boxes}
                title="No items yet"
                action={
                  canCreate ? (
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      <Button onClick={() => setNewOpen(true)}>
                        <Plus className="h-4 w-4" />
                        Add item
                      </Button>
                      <Button variant="outline" onClick={() => setImportOpen(true)}>
                        <Upload className="h-4 w-4" />
                        Import from spreadsheet
                      </Button>
                    </div>
                  ) : undefined
                }
              />
              )
            }
          />
        </CardContent>
      </Card>

      <NewItemDialog open={newOpen} onOpenChange={setNewOpen} defaultKind={kindSlug} />

      <ImportItemsDialog open={importOpen} onOpenChange={setImportOpen} />

      <DuplicateItemDialog
        open={duplicateSource !== null}
        onOpenChange={(open) => {
          if (!open) setDuplicateSource(null);
        }}
        source={duplicateSource}
      />

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleteItems.isPending) setPendingDelete(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingDelete && pendingDelete.items.length === 1
                ? 'Delete item?'
                : `Delete ${pendingDelete?.items.length ?? 0} items?`}
            </DialogTitle>
            <DialogDescription>
              {pendingDelete && pendingDelete.items.length === 1 ? (
                <>
                  This permanently removes{' '}
                  <span className="font-medium text-foreground">
                    {pendingDelete.items[0].name}
                  </span>{' '}
                  from the catalog. This can’t be undone.
                </>
              ) : (
                <>
                  This permanently removes the selected items from the catalog.
                  This can’t be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDelete(null)}
              disabled={deleteItems.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteItems.isPending}
            >
              {deleteItems.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Best-effort projection of a kind slug back to a human label, used
 * only as a fallback in the page header / dialog while the kinds
 * catalog query is still in flight. The catalog row's `label` is the
 * canonical display string everywhere else.
 */
function humanizeKindSlug(slug: string): string {
  return slug
    .split(/[_-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}
